import { http } from './http';
import { ROUTES } from './config';
import type { AvailableJob, JobApplicant, MyJobApplication, ScheduledJob } from '../types/domain';

/** SchedulingService client (manager+). The day board is the primary read model. */
export const schedulingApi = {
  getDay(date: string): Promise<{ date: string; jobs: ScheduledJob[] }> {
    return http.get(`${ROUTES.schedule}?date=${encodeURIComponent(date)}`);
  },

  getCustomerJobs(customerId: string): Promise<ScheduledJob[]> {
    return http.get(`${ROUTES.schedule}/by-customer/${customerId}`);
  },

  optimize(date: string): Promise<unknown> {
    return http.post(`${ROUTES.schedule}/optimize`, { date });
  },

  assignWorker(jobId: string, workerId: string, assignedBy = 'manual'): Promise<void> {
    return http.post(`${ROUTES.schedule}/assignments`, { jobId, workerId, assignedBy });
  },

  reschedule(jobId: string, newStart: string, reason: string, initiatedByAi = false): Promise<void> {
    return http.post(`${ROUTES.jobs}/${jobId}/reschedule`, { newStart, reason, initiatedByAi });
  },

  /** Cancel a single visit without cancelling the rest of a recurring booking. */
  cancelJob(jobId: string, reason: string): Promise<void> {
    return http.post(`${ROUTES.jobs}/${jobId}/cancel`, { reason });
  },

  // ── Worker job-application flow (worker|manager at the gateway) ──

  /** Manager opts an unassigned job into the applicant pool. */
  openForApplications(jobId: string): Promise<void> {
    return http.post(`${ROUTES.schedule}/jobs/${jobId}/open-for-applications`, {});
  },

  /** Real, open jobs a worker can apply to. */
  availableJobs(filters?: { service?: string; suburb?: string }): Promise<AvailableJob[]> {
    const params = new URLSearchParams();
    if (filters?.service) params.set('service', filters.service);
    if (filters?.suburb) params.set('suburb', filters.suburb);
    const qs = params.toString();
    return http.get(`${ROUTES.schedule}/available-jobs${qs ? `?${qs}` : ''}`);
  },

  /** Applicants for a job — manager only. */
  applicantsFor(jobId: string): Promise<JobApplicant[]> {
    return http.get(`${ROUTES.schedule}/jobs/${jobId}/applications`);
  },

  /** The signed-in worker's own application history. */
  myApplications(): Promise<MyJobApplication[]> {
    return http.get(`${ROUTES.schedule}/my-applications`);
  },

  /** Worker applies to an open job. */
  applyToJob(jobId: string): Promise<{ id: string }> {
    return http.post(`${ROUTES.schedule}/jobs/${jobId}/applications`, {});
  },

  /** Manager selects an applicant — performs the real assignment. */
  selectApplicant(jobId: string, applicationId: string): Promise<void> {
    return http.post(`${ROUTES.schedule}/jobs/${jobId}/applications/${applicationId}/select`, {});
  },

  /** Worker declines a job they were selected for — reopens it for other applicants. */
  declineAssignment(jobId: string): Promise<void> {
    return http.post(`${ROUTES.schedule}/jobs/${jobId}/decline`, {});
  },
};
